import { PropertyRecordCalculationStatus } from './../enums/property-record-calculation-status-enum';
import * as _ from 'lodash';
import { ClonableItem } from './clonable-item';
import {
    getPrettyTaskType,
    TaskStatus,
    getPrettyTaskStatus,
    TaskType,
    getPrettyTaskStatus2,
    getSimplePrettyTaskStatus
} from '../enums';
import { AttachmentLink } from './attachment-link';

import ITask = ZoningAPI.ITask;
import IStatisticByStatus = ZoningAPI.IStatisticByStatus;
import IAttachmentLink = ZoningAPI.IAttachmentLink;
import IPerformanceStatistic = ZoningAPI.IPerformanceStatistic;
import { ITaskModel } from '../interfaces/task-model.interface';
import { getTime } from '../utils/utils';
import { ProcessType } from 'src/app/modules/scheduler/enums/process-type-enum';

export class Task extends ClonableItem<ITask> implements ITaskModel {
    id: string;
    messages: any[];
    status: any;
    type: TaskType;
    zoningCodeId: number;
    zcVersionId: number;
    zcGridicsId: string;
    receivedRecords: number;
    emptyResultAttempts: number;
    expectedRecords: number;
    statsByStatus: IStatisticByStatus[];
    attachments?: IAttachmentLink[];
    steps?: ITaskModel[];
    zoningCodeRecords: number;
    currentExpectedRecords: number;
    progress: number;
    totalPropertyRecords: number;
    currentOffset: number;
    instanceId: number;
    state?: string;
    maxThreadsCount?: number;
    peakThreadsCount?: number;
    performance?: IPerformanceStatistic;
    name?: string;
    needToRecalculate?: boolean;
    isWaitingActionFinish?: boolean;
    place?: string;
    activeThreadsCount?: number;
    processType: ProcessType;
    isSelected: boolean = false;
    isArchived: boolean;
    isDestroyed: boolean;
    createdAt: string;
    updatedAt: string;
    maxPRsBunchSize?: number;
    stepIndex?: number;
    isProcess: boolean = true;

    constructor(value?: ITask) {
        super(value);
        this.initSteps();
    }

    get statusStr() {
        return `${this.typeStr} ${getPrettyTaskStatus(this.status)}`;
    }

    get simpleStatusStr() {
        return getSimplePrettyTaskStatus(this.status);
    }

    get prettyStatus() {
        return getPrettyTaskStatus2(this.status);
    }

    get typeStr() {
        return getPrettyTaskType(this.type);
    }

    get canStart() {
        return [TaskStatus.IDLE, TaskStatus.STOP, TaskStatus.PENDING_START].indexOf(this.status) >= 0;
    }

    get canRestart() {
        return this.canStart || this.status === TaskStatus.FINISHED;
    }

    get canStop() {
        return this.status === TaskStatus.PROCESSING;
    }

    get isIdle(): boolean {
        return this.status === TaskStatus.IDLE;
    }

    get attachmentsCount() {
        return _.get(this, 'attachments.length', 0);
    }

    get hasAttachments() {
        return !!this.attachmentsCount;
    }

    get stepsCount() {
        return _.get(this, 'steps.length', 0);
    }

    get hasSteps() {
        return !!this.stepsCount;
    }

    get isIndexing() {
        return Task.isIndexing(this);
    }

    get isCalculation() {
        return this.type === TaskType.ZC_VERSION_CALCULATION;
    }

    get iterationsPerHour() {
        return _.get(this, 'performance.iterationsPerHour', 0);
    }

    /**
     * @property {boolean} canRestart is true if this type of task supports Restart functionality
     */
    get isRestartSupported() {
        return (
            [
                TaskType.CALCULATE_FOLIOS,
                TaskType.CALCULATE_FOLIOS_MULTI_THREAD,
                TaskType.GET_AUTOMATION_DATA_JOB
            ].indexOf(this.type) >= 0
        );
    }

    get isClearThreadsSupported() {
        return (
            [
                TaskType.ZC_VERSION_CALCULATION,
                TaskType.CALCULATE_FOLIOS_MULTI_THREAD,
                TaskType.RECALCULATE_LISTED_BY_STATUS_JOB,
                TaskType.CALCULATE_ALL_TYPOLOGIES
            ].indexOf(this.type) >= 0
        );
    }

    get timeFromStartStr() {
        return this.getWorkingTime();
    }

    get timeLeftStr() {
        return this.getTimeLeft();
    }

    get totalTimeStr() {
        return this.getTotalTime();
    }

    get activeThreads() {
        return this.hasSteps
            ? Math.max(...this.steps.map(process => _.get(process, 'activeThreadsCount', 0)))
            : _.get(this, 'activeThreadsCount', 0);
    }

    get hasActiveThreads() {
        return this.activeThreads > 0;
    }

    get isWorking(): boolean {
        return this.status === TaskStatus.PROCESSING;
    }

    get isReplaceLandUseIdsProcess(): boolean {
        return this.type === TaskType.REPLACE_LAND_USE_IDS;
    }

    get allCalculatedPR(): number {
        if (!_.get(this.statsByStatus, 'length')) {
            return;
        }
        return this.statsByStatus.reduce((sum, stat) => {
            if (
                stat.status === PropertyRecordCalculationStatus.ERROR ||
                stat.status === PropertyRecordCalculationStatus.OK
            ) {
                return sum + stat.count;
            }
        }, 0);
    }

    get isCalcPRMultiThread() {
        return this.type === TaskType.CALCULATE_FOLIOS_MULTI_THREAD;
    }

    get isRemoveZoningCodeJob() {
        return this.type === TaskType.REMOVE_ZONING_CODE_JOB;
    }

    get isStep(): boolean {
        return _.isNumber(this.stepIndex);
    }

    get peakThreadsCountSafe(): number {
        return _.get(this, 'peakThreadsCount', this.maxThreadsCount);
    }

    initAttachments(attachments: IAttachmentLink[]) {
        if (!attachments || !attachments.length) {
            return;
        }
        this.attachments = attachments.map(attachment => new AttachmentLink(attachment));
    }

    initSteps() {
        if (!this.hasSteps) {
            return;
        }
        this.steps = this.steps.map(taskData => new Task(taskData));
    }

    initWithJSON(value: ITask, skipStatistic?: boolean) {
        /**
         * If current state of the model is newer then received value
         * then skip update.
         */
        if (this.updatedAt && value.updatedAt && new Date(this.updatedAt) > new Date(value.updatedAt)) {
            return;
        }
        if (skipStatistic) {
            this.clearStatisticsFields(value);
        }

        if (value) {
            Object.assign(this, value);
            this.initSteps();
        }

        this.calcPercentageRatio();
    }

    clearStatisticsFields(value: Object) {
        const skipStatisticsFields = [
            'zoningCodeRecords',
            'currentExpectedRecords',
            'progress',
            'totalPropertyRecords',
            'statsByStatus'
        ];

        skipStatisticsFields.forEach(property => delete value[property]);
    }

    getWorkingTime(): string {
        return getTime(_.get(this, 'performance.timeFromStart', 0));
    }

    getTimeLeft(): string {
        return getTime(_.get(this, 'performance.timeLeft', 0));
    }

    getTotalTime(): string {
        return getTime(_.get(this, 'performance.timeLeft', 0) + _.get(this, 'performance.timeFromStart', 0));
    }

    setMaxPRsBunchSize(maxPRsBunchSize: number) {
        this.maxPRsBunchSize = _.defaultTo(maxPRsBunchSize, 1);
    }

    static isTask(taskData: ITask): boolean {
        return taskData && taskData.processType === ProcessType.TASK;
    }

    static isJob(taskData: ITask): boolean {
        return taskData && [ProcessType.JOB, ProcessType.MULTI_STEP_JOB].indexOf(taskData.processType) >= 0;
    }

    static isIndexing(taskData: ITask): boolean {
        return [TaskType.ZONING_CODE_INDEXING, TaskType.ZONING_CODE_INDEXING_WITH_CURSOR].indexOf(taskData.type) >= 0;
    }

    calcPercentageRatio() {
        if (_.isEmpty(this.statsByStatus) || !this.zoningCodeRecords) {
            return;
        }
        this.statsByStatus.forEach(val => {
            val.ratio = Number(((val.count / this.zoningCodeRecords) * 100).toFixed(2));
        });
    }
}
